import { ChangeEvent } from 'react';
import { Trash2, Plus, GripVertical } from 'lucide-react';
import type { BulkSessionInput, BulkExerciseInput } from '../lib/api';

const DAY_LABELS: Record<number, string> = {
  1: 'Lun', 2: 'Mar', 3: 'Mer', 4: 'Gio', 5: 'Ven', 6: 'Sab', 7: 'Dom',
};

const FOCUS_PRESETS = ['push', 'pull', 'legs', 'fullbody', 'cardio', 'mobility', 'deload', 'tecnica'];

interface Props {
  session: BulkSessionInput;
  weeksTotal: number;
  index: number;
  onChange: (next: BulkSessionInput) => void;
  onRemove: () => void;
}

/**
 * Editor singola sessione: meta (week/day/name/focus/time/duration/note)
 * + lista esercizi (free-text). Riusabile dal wizard.
 */
export function WorkoutSessionEditor({ session, weeksTotal, index, onChange, onRemove }: Props) {
  const update = <K extends keyof BulkSessionInput>(key: K, value: BulkSessionInput[K]) => {
    onChange({ ...session, [key]: value });
  };

  const addExercise = () => {
    const nextPos = (session.exercises.reduce((m, e) => Math.max(m, e.position), 0)) + 1;
    const newEx: BulkExerciseInput = {
      id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      position: nextPos,
      name: '',
      variant: null,
      sets_planned: 3,
      reps_target: '8-12',
      rest_seconds: 90,
      notes: null,
    };
    onChange({ ...session, exercises: [...session.exercises, newEx] });
  };

  const updateExercise = (exIdx: number, patch: Partial<BulkExerciseInput>) => {
    const next = session.exercises.map((e, i) => (i === exIdx ? { ...e, ...patch } : e));
    onChange({ ...session, exercises: next });
  };

  const removeExercise = (exIdx: number) => {
    onChange({ ...session, exercises: session.exercises.filter((_, i) => i !== exIdx) });
  };

  const moveExercise = (exIdx: number, dir: -1 | 1) => {
    const newIdx = exIdx + dir;
    if (newIdx < 0 || newIdx >= session.exercises.length) return;
    const next = [...session.exercises];
    const tmp = next[exIdx];
    next[exIdx] = next[newIdx];
    next[newIdx] = tmp;
    // Riallinea position 1..N
    const reordered = next.map((e, i) => ({ ...e, position: i + 1 }));
    onChange({ ...session, exercises: reordered });
  };

  const intInputProps = (min: number, max: number) => ({
    type: 'number',
    min,
    max,
    step: 1,
  });

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 12,
        padding: 18,
        marginBottom: 14,
      }}
    >
      {/* Header riga sessione */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--mute)',
            letterSpacing: 1.2,
            textTransform: 'uppercase',
          }}
        >
          Sessione #{index + 1}
        </div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onRemove}
          aria-label="Rimuovi sessione"
          style={{
            border: 0,
            background: 'transparent',
            color: 'var(--red)',
            cursor: 'pointer',
            padding: 6,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <Trash2 size={14} /> Rimuovi sessione
        </button>
      </div>

      {/* Meta */}
      <div style={{ display: 'grid', gridTemplateColumns: '90px 90px 1fr', gap: 10, marginBottom: 10 }}>
        <Field label="Settimana">
          <input
            {...intInputProps(1, weeksTotal)}
            value={session.week_n}
            onChange={(e: ChangeEvent<HTMLInputElement>) => update('week_n', Math.max(1, Math.min(weeksTotal, parseInt(e.target.value, 10) || 1)))}
            style={inputStyle}
          />
        </Field>
        <Field label="Giorno">
          <select
            value={session.day_n}
            onChange={(e) => update('day_n', parseInt(e.target.value, 10))}
            style={inputStyle}
          >
            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
              <option key={d} value={d}>{DAY_LABELS[d]}</option>
            ))}
          </select>
        </Field>
        <Field label="Nome sessione">
          <input
            type="text"
            value={session.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="es. Schiena pesante"
            style={inputStyle}
          />
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 110px', gap: 10, marginBottom: 10 }}>
        <Field label="Focus">
          <input
            type="text"
            list={`focus-presets-${session.id || index}`}
            value={session.focus ?? ''}
            onChange={(e) => update('focus', e.target.value || null)}
            placeholder="push, pull, legs..."
            style={inputStyle}
          />
          <datalist id={`focus-presets-${session.id || index}`}>
            {FOCUS_PRESETS.map((f) => <option key={f} value={f} />)}
          </datalist>
        </Field>
        <Field label="Ora">
          <input
            type="time"
            value={session.scheduled_time ?? ''}
            onChange={(e) => update('scheduled_time', e.target.value || null)}
            style={inputStyle}
          />
        </Field>
        <Field label="Durata (min)">
          <input
            {...intInputProps(1, 600)}
            value={session.duration_estimated_min ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              update('duration_estimated_min', v === '' ? null : parseInt(v, 10));
            }}
            placeholder="60"
            style={inputStyle}
          />
        </Field>
      </div>

      <Field label="Nota coach (mostrata all'atleta)">
        <textarea
          value={session.coach_note ?? ''}
          onChange={(e) => update('coach_note', e.target.value || null)}
          rows={2}
          placeholder="es. Focus tecnica, riscaldamento accurato"
          style={{ ...inputStyle, fontFamily: 'var(--sans)', resize: 'vertical' }}
        />
      </Field>

      {/* Esercizi */}
      <div
        style={{
          marginTop: 14,
          paddingTop: 14,
          borderTop: '1px dashed var(--line)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 700,
            color: 'var(--mute)',
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            marginBottom: 10,
          }}
        >
          Esercizi · {session.exercises.length}
        </div>

        {session.exercises.length === 0 ? (
          <div
            style={{
              padding: '20px 16px',
              background: 'var(--surface-2)',
              borderRadius: 8,
              color: 'var(--mute)',
              fontSize: 13,
              textAlign: 'center',
            }}
          >
            Nessun esercizio. Clicca "Aggiungi esercizio".
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {session.exercises.map((ex, i) => (
              <div
                key={ex.id ?? `ex-${i}`}
                style={{
                  background: 'var(--surface-2)',
                  border: '1px solid var(--line-3)',
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <button
                      type="button"
                      onClick={() => moveExercise(i, -1)}
                      disabled={i === 0}
                      aria-label="Sposta su"
                      style={{ ...arrowBtn, opacity: i === 0 ? 0.3 : 1 }}
                    >▲</button>
                    <button
                      type="button"
                      onClick={() => moveExercise(i, 1)}
                      disabled={i === session.exercises.length - 1}
                      aria-label="Sposta giù"
                      style={{ ...arrowBtn, opacity: i === session.exercises.length - 1 ? 0.3 : 1 }}
                    >▼</button>
                  </div>
                  <GripVertical size={14} color="var(--mute)" />
                  <div
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--mute)',
                      width: 28,
                    }}
                  >#{ex.position}</div>
                  <input
                    type="text"
                    value={ex.name}
                    onChange={(e) => updateExercise(i, { name: e.target.value })}
                    placeholder="Esercizio (es. Squat)"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <input
                    type="text"
                    value={ex.variant ?? ''}
                    onChange={(e) => updateExercise(i, { variant: e.target.value || null })}
                    placeholder="variante (opz.)"
                    style={{ ...inputStyle, width: 140 }}
                  />
                  <button
                    type="button"
                    onClick={() => removeExercise(i)}
                    aria-label="Rimuovi esercizio"
                    style={{
                      border: 0,
                      background: 'transparent',
                      color: 'var(--red)',
                      cursor: 'pointer',
                      padding: 6,
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  ><Trash2 size={14} /></button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '80px 110px 100px 1fr', gap: 8 }}>
                  <Field label="Set" small>
                    <input
                      {...intInputProps(1, 30)}
                      value={ex.sets_planned}
                      onChange={(e) => updateExercise(i, { sets_planned: Math.max(1, Math.min(30, parseInt(e.target.value, 10) || 1)) })}
                      style={inputStyleSmall}
                    />
                  </Field>
                  <Field label="Reps" small>
                    <input
                      type="text"
                      value={ex.reps_target ?? ''}
                      onChange={(e) => updateExercise(i, { reps_target: e.target.value || null })}
                      placeholder="8-12"
                      style={inputStyleSmall}
                    />
                  </Field>
                  <Field label="Rest (s)" small>
                    <input
                      {...intInputProps(0, 1800)}
                      value={ex.rest_seconds ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        updateExercise(i, { rest_seconds: v === '' ? null : parseInt(v, 10) });
                      }}
                      placeholder="90"
                      style={inputStyleSmall}
                    />
                  </Field>
                  <Field label="Note" small>
                    <input
                      type="text"
                      value={ex.notes ?? ''}
                      onChange={(e) => updateExercise(i, { notes: e.target.value || null })}
                      placeholder="es. tempo 3-1-1"
                      style={inputStyleSmall}
                    />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={addExercise}
          style={{
            marginTop: 10,
            border: '1px dashed var(--line)',
            background: 'transparent',
            color: 'var(--ink-2)',
            padding: '8px 14px',
            borderRadius: 8,
            fontSize: 12.5,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Plus size={14} /> Aggiungi esercizio
        </button>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid var(--line)',
  borderRadius: 6,
  padding: '7px 10px',
  fontSize: 13,
  background: 'var(--surface)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  boxSizing: 'border-box',
};

const inputStyleSmall: React.CSSProperties = {
  ...inputStyle,
  fontSize: 12,
  padding: '5px 8px',
};

const arrowBtn: React.CSSProperties = {
  border: 0,
  background: 'transparent',
  color: 'var(--mute)',
  cursor: 'pointer',
  fontSize: 9,
  padding: '1px 4px',
  lineHeight: 1,
};

function Field({ label, small, children }: { label: string; small?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontFamily: 'var(--mono)',
          fontSize: small ? 9.5 : 10.5,
          fontWeight: 700,
          color: 'var(--mute)',
          letterSpacing: 1.2,
          textTransform: 'uppercase',
        }}
      >{label}</span>
      {children}
    </label>
  );
}
